import React from "react";
import {Form, Input, Slider, Tooltip, Typography} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import {numberFormat} from "../ui/tokens";

const RESEARCH_LINK = 'https://www.annalsofoncology.org/article/S0923-7534(20)39796-9/fulltext'

/**
 * Параметр сценария: слайдер и поле процента для стратегий А и В плюс
 * пересчёт в число пациентов.
 *
 * Вёрстка на CSS-grid: раньше строка собиралась из вложенных Col antd с
 * жёсткими долями (4/11/1/8 и 4/12/3/3), из-за чего подписи наползали друг
 * на друга и ломались на любой нестандартной ширине.
 *
 * Поведение сохранено: слайдер и поле привязаны к одному полю формы
 * (prefix + 'A' / 'B'); при заданном changeGlobal значения проставляются
 * сразу нескольким префиксам, а поле процента становится расчётным.
 */
export const StrategyParam = ({title, hint, prefix, one, strategy, primary, changeGlobal, setStrategy, mx = 1}) => {
    const candidate = strategy[prefix + 'CV']
        || (changeGlobal?.length && changeGlobal.reduce((prev, curr) => prev + Number(strategy[curr + 'CV']), 0))

    const doSetModel = (postfix, value) => {
        const newStrategy = {...strategy}
        for (const key of changeGlobal) newStrategy[key + postfix] = value
        newStrategy[prefix + postfix] = value
        setStrategy(newStrategy)
    }

    const onChange = {
        A: changeGlobal ? (data) => doSetModel('A', data) : () => {},
        B: changeGlobal ? (data) => doSetModel('B', data) : () => {},
    }

    // сколько пациентов стоит за выбранным процентом
    const people = (suffix) => {
        if (changeGlobal?.length)
            return changeGlobal.reduce((prev, curr) =>
                prev + Math.round(mx * Number(strategy[curr + 'CV']) * Number(strategy[curr + suffix]) / 100), 0)
        if (!candidate || isNaN(candidate)) return null
        return Math.round(mx * candidate * strategy[prefix + suffix] / 100)
    }

    // при агрегате процент считается из долей, поэтому поле только для чтения
    const aggregatePercent = (suffix) => Math.round(
        100 * changeGlobal.reduce((prev, curr) =>
            prev + Math.round(mx * Number(strategy[curr + 'CV']) * Number(strategy[curr + suffix]) / 100), 0) / candidate)

    // подсказка стоит в строке заголовка: вынесенная отдельно, она повисала
    // пустой строкой под длинным названием параметра
    const head = (
        <div className={"param__head"}>
            <Typography.Text strong={primary} className={"param__title"}>
                {title}{' '}
                <Tooltip title={hint || "Источник данных по параметру"}>
                    <a href={RESEARCH_LINK} target="_blank" rel="noreferrer" className={"param__info"}>
                        <InfoCircleOutlined/>
                    </a>
                </Tooltip>
            </Typography.Text>
            {candidate ? <span className={"param__potential"}>
                потенциал {numberFormat.format(Math.round(mx * candidate))} чел.
            </span> : null}
        </div>
    )

    if (one) return (
        <div className={"param param--single"}>
            {head}
            <Form.Item name={prefix} noStyle>
                <Input suffix={"%"} className={"param__single-input"}/>
            </Form.Item>
        </div>
    )

    const line = (suffix, label, tone) => {
        const count = people(suffix)
        return (
            <div className={"param__line"} key={suffix}>
                <span className={`param__badge param__badge--${tone}`}>{label}</span>
                <Form.Item name={prefix + suffix} noStyle>
                    <Slider onChange={onChange[suffix]} tooltipVisible={false} className={"param__slider"}/>
                </Form.Item>
                <div className={"param__value"}>
                    {changeGlobal?.length
                        ? <Input suffix={"%"} disabled value={aggregatePercent(suffix)}/>
                        : <Form.Item name={prefix + suffix} noStyle>
                            <Input suffix={"%"} onChange={onChange[suffix]}/>
                        </Form.Item>}
                </div>
                <div className={"param__people"}>
                    {count === null ? '' : `${numberFormat.format(count)} чел.`}
                </div>
            </div>
        )
    }

    return (
        <div className={"param"}>
            {head}
            {line('A', 'А', 'a')}
            {line('B', 'В', 'b')}
            <div style={{display: 'none'}}>
                <Form.Item name={prefix + 'CV'}><Input/></Form.Item>
            </div>
        </div>
    )
}

export default StrategyParam
